import React, { useState } from 'react';
import Toast from '../../Toast/Toast';
import { groupOf } from '../state/emptyDoc';
import { usePreferences } from '../state/usePreferences';
import { useCreativePreferencesStore } from '../state/store';
import { splitVideoFields } from '../fields/videoLayout';
const Arrow = ({
  dir
}) => React.createElement("svg", {
  width: "16",
  height: "16",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "2.2",
  strokeLinecap: "round",
  strokeLinejoin: "round"
}, React.createElement("path", {
  d: dir === 'prev' ? 'M15 18l-6-6 6-6' : 'M9 6l6 6-6 6'
}));

// One bar shape across every section: Prev · centred status · Next. The counts
// and their wording come from the payload's own field labels and caps.
const BottomBar = () => {
  const {
    doc,
    sections,
    index,
    section,
    prevKey,
    nextKey,
    goTo,
    tab,
    setTab
  } = usePreferences();
  const {
    cpAwaitingChoice
  } = useCreativePreferencesStore();
  const [toast, setToast] = useState('');
  if (!section) return null;
  const {
    isTabbed,
    picker,
    sources
  } = splitVideoFields(section);
  // A tabbed section is two stops in the flow, not one: Next walks to the
  // second tab before it leaves the section, and Prev walks back the same way.
  const tabs = isTabbed ? 2 : 1;
  const hasNext = tab < tabs - 1 || !!nextKey;
  const hasPrev = tab > 0 || !!prevKey;
  // How many stops the previous section has, so Prev enters it on its last.
  const previous = index > 0 ? sections[index - 1] : null;
  const prevTabs = previous && splitVideoFields(previous).isTabbed ? 2 : 1;
  // Only this section's own options can hold it up, so an unanswered card
  // left behind cannot block a section that has nothing to do with it.
  const ownIds = (section.fields || []).reduce((acc, f) => acc.concat((f.options || []).map(o => String(o.id))), []);
  const unchosen = Object.keys(cpAwaitingChoice || {}).filter(id => ownIds.includes(String(id)));
  const goNext = () => {
    if (unchosen.length) {
      setToast('Tell us which playlist you want for every music style you have ticked.');
      return;
    }
    return tab < tabs - 1 ? setTab(tab + 1) : goTo(nextKey);
  };
  const goPrev = () => tab > 0 ? setTab(tab - 1) : goTo(prevKey, prevTabs - 1);
  const group = groupOf(doc, section.key);
  // Only a source field's options are not answers — the client picks videos
  // out of them, so the option's own id must not be counted. See ReelPicker.
  // Everywhere else the option ids are exactly what the answer holds.
  const answersIn = field => {
    const answers = group[field.key] || [];
    if (!field.options_are_sources) return answers;
    const own = (field.options || []).map(o => String(o.id));
    return answers.filter(x => !own.includes(String(x)));
  };
  // Tabs are two separate stops, so each reports only its own fields. Music's
  // card field keeps its max_picks, so the pill reports it as normal.
  // Highlights counts playlists only: videos inside one are not picked.
  const tabFields = tab === 0 ? [picker] : sources;
  const visible = isTabbed ? tabFields.filter(Boolean) : section.fields || [];
  // A tab reports its own field whether or not ops has given it a cap: with
  // no cap on the playlists, Highlights would otherwise count nothing at all.
  // A list field ops added by hand has no options behind it, so its boxes
  // are written in rather than picked and the bar has no picks to report.
  const counted = visible.filter(f => f.kind === 'list' && (f.options || []).length && (f.max_picks || isTabbed));
  // One element per count rather than one joined string: a phone has to
  // wrap this, and a count has to stay on the same line as the label it
  // counts.
  const pill = counted.map(f => {
    const n = answersIn(f).length;
    return f.max_picks ? `${n} / ${f.max_picks} ${f.label}` : `${n} ${f.label}`;
  });
  const atCap = counted.some(f => f.max_picks && answersIn(f).length >= f.max_picks);
  return React.createElement("div", {
    className: "cp-bar"
  }, React.createElement("button", {
    type: "button",
    className: "cp-nav",
    disabled: !hasPrev,
    onClick: goPrev,
    "aria-label": "Previous section"
  }, React.createElement(Arrow, {
    dir: "prev"
  })), pill.length ? React.createElement("div", {
    className: "cp-bar-status"
  }, React.createElement("span", {
    className: "cp-pill"
  }, pill.map((text, i) => React.createElement("span", {
    key: i,
    className: "cp-pill-item"
  }, text))), atCap && React.createElement("span", {
    className: "cp-bar-line"
  }, "Limit reached — deselect one to swap")) : React.createElement("div", {
    className: "cp-bar-spacer"
  }), React.createElement("button", {
    type: "button",
    className: "cp-nav",
    disabled: !hasNext,
    onClick: goNext,
    "aria-label": "Next section"
  }, React.createElement(Arrow, {
    dir: "next"
  })), toast && React.createElement(Toast, {
    message: toast,
    onClose: () => setToast('')
  }));
};
export default BottomBar;
